import {
  ChangeDetectionStrategy,
  Component,
  signal,
  TemplateRef,
  viewChild,
  OnDestroy,
  inject,
} from '@angular/core';
import {
  FormGroup,
  FormControl,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { GroupDetails } from '../group-model';
import { AddIconComponent } from '@kuiper/icons';
import { TranslocoPipe } from '@jsverse/transloco';
import { UserData } from '../../users-database/user-model';
import { BaseAssignedList } from '../../base/base-assigned-list';
import { DualLangInputComponent } from '@areeb-innovative-technologies/angular_multi_languages_input_lib';
import { RouterLink } from '@angular/router';
import { Subscription } from 'rxjs';
import { OverlayLoaderComponent } from '../../../../../common/components/overlay-loader/overlay-loader.component';
import { FeatureFlagsService } from '../../../../../common/services/feature-flags/feature-flags.service';
import { TenantService } from '../../../../../common/services/tenant/tenant.service';
import { TenantPathPipe } from '../../../../../common/pipes/tenant-path/tenant-path.pipe';
import { ScrollToErrorDirective } from '../../../../../common/directives/scroll-to-error/scroll-to-error.directive';
import { UploadFilesComponent } from '../../../../../../ui-components/lib/upload-files/upload-files.component';
import { PaginatorComponent } from '../../../../../../ui-components/lib/paginator/paginator.component';
import { AssignGroupUsersComponent } from '../../../../../common/components/assign-group-users/assign-group-users.component';
import { ValidationMessageComponent } from '../../../../../../ui-components/lib/validation-message/validation-message.component';
import { GroupUserCardComponent } from '../../../../../common/components/group-user-card/group-user-card.component';
import { LoaderComponent } from '../../../../../../ui-components/lib/loader/loader.component';
import { ADMIN_ROUTES } from '../../../../../common/constants/admin.routing.constants';
import { uploadFileUrls } from '../../../../../common/constants/upload-file-url';
import { Attachment } from '../../../../../../ui-components/lib/upload-files/attachment.interface';
import { dualLanguageCustomValidators } from '../../../../../../form-builder/services/form/custom-validators';
import { FormValidation } from '../../../../../../ui-components/enums/form-validation';
import { ApiResponse } from '../../../../../common/models/api-response.model';
import { Option } from '../../../../../common/models/option.model';

@Component({
  selector: 'lib-group-form',
  imports: [
    OverlayLoaderComponent,
    ReactiveFormsModule,
    ValidationMessageComponent,
    TranslocoPipe,
    GroupUserCardComponent,
    AssignGroupUsersComponent,
    PaginatorComponent,
    LoaderComponent,
    DualLangInputComponent,
    UploadFilesComponent,
    RouterLink,
    ScrollToErrorDirective,
    AddIconComponent,
    TenantPathPipe,
  ],
  templateUrl: './group-form.component.html',
  styleUrl: './group-form.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class GroupFormComponent extends BaseAssignedList implements OnDestroy {
  readonly ADMIN_ROUTES = ADMIN_ROUTES;

  readonly tenantService = inject(TenantService);
  readonly featureFlags = inject(FeatureFlagsService);

  subscriptions = new Subscription();
  assignGroupUsersDialog = viewChild<TemplateRef<unknown>>(
    'assignGroupUsersDialog'
  );
  scrollToError = viewChild.required(ScrollToErrorDirective);
  groupId = signal<string>('');
  groupFormGroup = signal<FormGroup>(new FormGroup({}));
  groupDetails!: GroupDetails;
  uploadFileUrls = uploadFileUrls;
  uploadedImage = signal<Attachment[]>([]);

  constructor() {
    super();

    this.createForm();
    this.handleRouteParams();
  }

  createForm() {
    this.groupFormGroup.set(
      new FormGroup({
        image: new FormControl(''),
        name: new FormGroup(
          {
            en: new FormControl('', {
              validators: [Validators.maxLength(FormValidation.maxLength)],
            }),
            ar: new FormControl('', {
              validators: [Validators.maxLength(FormValidation.maxLength)],
            }),
          },
          {
            validators: [dualLanguageCustomValidators.atLeastOneLangRequired],
          }
        ),
      })
    );
  }

  handleRouteParams() {
    this.subscriptions.add(
      this.route.params.subscribe(params => {
        this.setTextBasedOnId(params['id']);

        if (params['id']) {
          this.groupId.set(params['id']);
          this.getGroupDetails();
          this.getAssignedCardList();
        } else {
          this.isLoading.set(false);
        }
      })
    );
  }

  setTextBasedOnId(paramsId: string) {
    this.pageTitle.set(
      paramsId
        ? this.transloco.translate('group_form_screen_edit_title')
        : this.transloco.translate('group_form_screen_add_title')
    );
    this.buttonText.set(
      paramsId
        ? this.transloco.translate('group_form_screen_save_edits_button')
        : this.transloco.translate('group_form_screen_add_group_button')
    );
  }

  getGroupDetails() {
    this.subscriptions.add(
      this.groupService.getGroup(this.groupId()).subscribe({
        next: (response: { data: GroupDetails }) =>
          this.handleGroupDetailsSuccessResponse(response),
        error: () => {
          this.isLoading.set(false);
          this.router.navigate([
            this.tenantService.adminPath(`${ADMIN_ROUTES.GROUPS.LIST}`),
          ]);
        },
      })
    );
  }

  handleGroupDetailsSuccessResponse(response: { data: GroupDetails }) {
    this.groupDetails = response.data;
    this.editMode.set(true);
    this.patchForm(response.data);
  }

  getAssignedCardList() {
    this.isAssignedListLoading.set(true);
    this.subscriptions.add(
      this.groupService
        .getGroupUsers(this.groupId(), this.assignedListCurrentPage(), 15)
        .subscribe({
          next: (response: ApiResponse<UserData>) =>
            this.handleGroupAssignedUsersResponse(response),
        })
    );
  }

  handleGroupAssignedUsersResponse(response: ApiResponse<UserData>) {
    this.assignedList.set(this.mapAssignedUsers(response.data));
    this.handleAssignedListResponse(response.meta);
  }

  mapAssignedUsers(users: UserData[]): Option[] {
    return users
      .map(user => {
        const userOption: Option = {
          label: user.name,
          value: user.id.toString(),
          image: user.image,
          job_title: user.placement?.job_title?.name || '',
        };
        return userOption;
      })
      .sort((a, b) => {
        return a.label.localeCompare(b.label);
      });
  }

  handleAssignedListPagination(pageNumber: number) {
    this.assignedListCurrentPage.set(pageNumber);
    this.getAssignedCardList();
  }

  patchForm(data: GroupDetails) {
    setTimeout(() => {
      this.groupFormGroup().get('name.en')?.setValue(data.translations.name.en);
      this.groupFormGroup().get('name.ar')?.setValue(data.translations.name.ar);
      if (data.image?.url) {
        this.groupFormGroup()
          .get('image')
          ?.setValue(data.image ? [data.image.id] : null);

        this.uploadedImage.set([data.image as Attachment]);
      }
      this.isLoading.set(false);
    }, 10);
  }

  submit() {
    this.formService.syncDualLangControls(this.groupFormGroup());

    if (this.groupFormGroup().invalid) {
      this.groupFormGroup().markAllAsTouched();
      this.scrollToError().scrollToFirstError();
    } else {
      this.isLoading.set(true);

      const groupForm = {
        ...this.groupFormGroup().getRawValue(),
        image: this.groupFormGroup().value['image']
          ? this.groupFormGroup().value['image'][0]
          : null,
        added_users: this.addedList().length
          ? this.addedList().map(item => +item.value)
          : [],
        removed_users: this.removedList().length
          ? this.removedList().map(item => +item.value)
          : [],
      };

      if (this.editMode()) {
        this.updateGroup(groupForm);
      } else {
        this.addGroup(groupForm);
      }
    }
  }

  updateGroup(groupForm: any) {
    this.subscriptions.add(
      this.groupService.updateGroup(groupForm, this.groupDetails.id).subscribe({
        next: () => this.handleSuccess(),
        error: error => this.handleError(error),
      })
    );
  }

  addGroup(groupForm: any) {
    this.subscriptions.add(
      this.groupService.addGroup(groupForm).subscribe({
        next: () => this.handleSuccess(),
        error: error => this.handleError(error),
      })
    );
  }

  handleError(error: any) {
    this.isLoading.set(false);
    this.toaster.add(
      error.message ? error.message : error.error.message,
      '',
      '',
      'error',
      true,
      true
    );
    this.formService.addParentFormGroupErrors(error, this.groupFormGroup());
    this.scrollToError().scrollToFirstError(false);
  }

  openAssignGroupUsersDialog() {
    this.currentPage.set(1);
    this.selectedList.set([]);
    this.getGroupUsers();

    this.modalService.modalContent.set({
      isOpen: true,
      customizeContent: this.assignGroupUsersDialog(),
      canNotCloseFromOutside: true,
      width: '51',
    });
  }

  getGroupUsers(name = '', page = 1) {
    this.currentPage.set(page);
    this.isLoadingGroupUsers.set(true);
    const lookupFilters = this.groupDetails?.id
      ? {
          name,
          page: this.currentPage(),
          filtersNotState: 'inactive',
          filtersNotStateCancelled: 'cancelled',
          filtersNotGroup: this.groupDetails.id,
          perPage: 25,
        }
      : {
          name,
          page: this.currentPage(),
          filtersNotState: 'inactive',
          filtersNotStateCancelled: 'cancelled',
          perPage: 25,
        };

    this.subscriptions.add(
      this.userService
        .userLookup(lookupFilters)
        .subscribe(response => this.handleUsersLookupResponse(response, name))
    );
  }

  navigateToList() {
    this.router.navigate([
      this.tenantService.adminPath(`${ADMIN_ROUTES.GROUPS.LIST}`),
    ]);
  }

  navigateToDetails() {
    this.router.navigate([
      this.tenantService.adminPath(`${ADMIN_ROUTES.GROUPS.DETAILS}`),
      this.groupId(),
    ]);
  }

  selectItem(item: Option) {
    this.baseSelectItem(item);

    if (
      this.currentPage() < this.totalPages() &&
      this.lookupList().length < 7
    ) {
      this.getGroupUsers('', this.currentPage() + 1);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
